import type { RewardPackage, RewardPackageItem } from 'types/rewards'
import { DeleteOutlined, EditOutlined, PlusOutlined, CopyOutlined, UserAddOutlined } from '@ant-design/icons'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import ButtonActionBar from 'components/ButtonActionBar/ButtonActionBar'
import useAuth from 'hooks/useAuth'
import useLocalStorage from 'hooks/useLocalStorage'
import {
  useFetchRewardPackages,
  useDeleteRewardPackage,
  useDeleteRewardPackages,
  useCopyRewardPackage,
  useAssignRewardPackage,
  invalidateRewardPackagesCache,
} from 'hooks/useRewardPackages'
import React, { Suspense, startTransition, useCallback, useEffect, useMemo, useState, useTransition } from 'react'
import { useNavigate } from 'react-router-dom'
import { getRootOrganizationId } from 'types/user-profile'
import AssignPackageDialog from './AssignPackageDialog'
import CopyPackageDialog from './CopyPackageDialog'

const RewardPackagesSubComponent: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(new Set())
  const [isPending, startTransitionHook] = useTransition()

  // Pagination and sorting state with local storage persistence
  const [page, setPage] = useLocalStorage<number>('reward-packages-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('reward-packages-rows-per-page', 25, profile?.user?.id)
  const [sort, setSort] = useLocalStorage<string>('reward-packages-sort', 'name', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('reward-packages-order', 'asc', profile?.user?.id)

  // Copy dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [packageToCopy, setPackageToCopy] = useState<RewardPackage | null>(null)

  // Assign dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [packageToAssign, setPackageToAssign] = useState<RewardPackage | null>(null)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [packageToDelete, setPackageToDelete] = useState<RewardPackage | null>(null)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  // Hooks
  const { packages, isLoading, refetch } = useFetchRewardPackages(
    rootOrganizationId,
    token!,
    !!rootOrganizationId && !!token
  )
  const { deletePackage } = useDeleteRewardPackage(token!)
  const { deletePackages, isLoading: isDeletingPackages } = useDeleteRewardPackages(token!)
  const { copyPackage, isLoading: isCopyingPackage } = useCopyRewardPackage(token!)
  const { assignPackage, isLoading: isAssigningPackage } = useAssignRewardPackage(token!)

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Filter and sort packages
  const filteredPackages = useMemo(() => {
    if (!packages) return []
    let result = [...packages]

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter((pkg) => pkg.name.toLowerCase().includes(term))
    }

    // Sort
    result.sort((a, b) => {
      let aVal: any = a[sort as keyof RewardPackage]
      let bVal: any = b[sort as keyof RewardPackage]

      if (typeof aVal === 'string') aVal = aVal.toLowerCase()
      if (typeof bVal === 'string') bVal = bVal.toLowerCase()

      if (aVal < bVal) return order === 'asc' ? -1 : 1
      if (aVal > bVal) return order === 'asc' ? 1 : -1
      return 0
    })

    return result
  }, [packages, searchTerm, sort, order])

  // Paginated packages
  const paginatedPackages = useMemo(() => {
    const start = page * rowsPerPage
    return filteredPackages.slice(start, start + rowsPerPage)
  }, [filteredPackages, page, rowsPerPage])

  // Handle sorting
  const handleSort = useCallback(
    (property: string) => {
      const isAscending = sort === property && order === 'asc'
      setOrder(isAscending ? 'desc' : 'asc')
      setSort(property)
    },
    [sort, order, setSort, setOrder]
  )

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      startTransitionHook(() => {
        setPage(newPage)
      })
    },
    [setPage]
  )

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      startTransitionHook(() => {
        setRowsPerPage(Number.parseInt(event.target.value, 10))
        setPage(0)
      })
    },
    [setRowsPerPage, setPage]
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value)
      setPage(0)
    },
    [setPage]
  )

  // Navigation handlers
  const handleCreatePackage = useCallback(() => {
    startTransition(() => navigate('/rewards/packages/create'))
  }, [navigate])

  const handleEditPackage = useCallback(
    (pkg: RewardPackage) => {
      startTransition(() => navigate('/rewards/packages/edit', { state: { package: pkg } }))
    },
    [navigate]
  )

  const handleOpenDeleteDialog = useCallback((pkg: RewardPackage) => {
    setPackageToDelete(pkg)
    setDeleteDialogOpen(true)
  }, [])

  const handleCloseDeleteDialog = useCallback(() => {
    setPackageToDelete(null)
    setDeleteDialogOpen(false)
  }, [])

  const handleConfirmDeletePackage = useCallback(async () => {
    if (!packageToDelete) return
    try {
      await deletePackage(packageToDelete.id!)
      setSnackbar({ open: true, message: 'Package deleted successfully', severity: 'success' })
      invalidateRewardPackagesCache(rootOrganizationId, token!)
      refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete package', severity: 'error' })
    } finally {
      handleCloseDeleteDialog()
    }
  }, [packageToDelete, deletePackage, rootOrganizationId, token, refetch, handleCloseDeleteDialog])

  // Copy handlers
  const handleOpenCopyDialog = useCallback((pkg: RewardPackage) => {
    setPackageToCopy(pkg)
    setCopyDialogOpen(true)
  }, [])

  const handleCloseCopyDialog = useCallback(() => {
    setPackageToCopy(null)
    setCopyDialogOpen(false)
  }, [])

  const handleCopyPackage = useCallback(
    async (newName: string) => {
      if (!packageToCopy) return
      try {
        await copyPackage(packageToCopy.id!, newName)
        setSnackbar({ open: true, message: 'Package copied successfully', severity: 'success' })
        handleCloseCopyDialog()
        invalidateRewardPackagesCache(rootOrganizationId, token!)
        refetch()
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to copy package', severity: 'error' })
      }
    },
    [packageToCopy, copyPackage, handleCloseCopyDialog, rootOrganizationId, token, refetch]
  )

  // Assign handlers
  const handleOpenAssignDialog = useCallback((pkg: RewardPackage) => {
    setPackageToAssign(pkg)
    setAssignDialogOpen(true)
  }, [])

  const handleCloseAssignDialog = useCallback(() => {
    setPackageToAssign(null)
    setAssignDialogOpen(false)
  }, [])

  const handleAssignPackage = useCallback(
    async (userId: string, periodId: string, rootPeriodId: string, organizationId: string) => {
      if (!packageToAssign) return
      try {
        await assignPackage({
          rootOrganizationId,
          organizationId,
          rootPeriodId,
          periodId,
          userId,
          packageId: packageToAssign.id!,
        })
        setSnackbar({ open: true, message: 'Package assigned successfully', severity: 'success' })
        handleCloseAssignDialog()
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to assign package', severity: 'error' })
      }
    },
    [packageToAssign, assignPackage, rootOrganizationId, handleCloseAssignDialog]
  )

  // Selection handlers
  const handleTogglePackage = useCallback((packageId: string) => {
    setSelectedPackages((prev) => {
      const next = new Set(prev)
      if (next.has(packageId)) {
        next.delete(packageId)
      } else {
        next.add(packageId)
      }
      return next
    })
  }, [])

  const handleToggleAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!paginatedPackages) return
      const allIds = paginatedPackages.map((p) => p.id!)

      setSelectedPackages((prev) => {
        const next = new Set(prev)
        if (event.target.checked) {
          allIds.forEach((id) => next.add(id))
        } else {
          allIds.forEach((id) => next.delete(id))
        }
        return next
      })
    },
    [paginatedPackages]
  )

  const handleOpenBulkDeleteDialog = useCallback(() => {
    setBulkDeleteDialogOpen(true)
  }, [])

  const handleCloseBulkDeleteDialog = useCallback(() => {
    setBulkDeleteDialogOpen(false)
  }, [])

  const handleConfirmDeleteSelectedPackages = useCallback(async () => {
    try {
      await deletePackages(Array.from(selectedPackages))
      setSnackbar({
        open: true,
        message: `${selectedPackages.size} packages deleted successfully`,
        severity: 'success',
      })
      setSelectedPackages(new Set())
      invalidateRewardPackagesCache(rootOrganizationId, token!)
      refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete packages', severity: 'error' })
    } finally {
      handleCloseBulkDeleteDialog()
    }
  }, [selectedPackages, deletePackages, rootOrganizationId, token, refetch, handleCloseBulkDeleteDialog])

  const handleClearSelection = useCallback(() => {
    setSelectedPackages(new Set())
  }, [])

  // Helper to get rewards summary
  const getRewardsSummary = (items: RewardPackageItem[]) => {
    const company = items.reduce((acc, item) => acc + item.companyContribution, 0)
    const employee = items.reduce((acc, item) => acc + item.employeeContribution, 0)
    return `${items.length} rewards, $${company.toLocaleString()} company, $${employee.toLocaleString()} employee`
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  const allSelected = paginatedPackages.length > 0 && paginatedPackages.every((p) => selectedPackages.has(p.id!))
  const someSelected = paginatedPackages.some((p) => selectedPackages.has(p.id!)) && !allSelected

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Reward Packages" />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="packages-search"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        {filteredPackages.length === 0 && !isPending ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              {searchTerm ? 'No packages match your search' : 'No reward packages yet'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
              <Table stickyHeader size="small" aria-label="packages table">
                <TableHead
                  sx={{
                    '& th': {
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderBottom: `2px solid ${theme.palette.divider} !important`,
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ width: 50, position: 'sticky !important' }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleToggleAll}
                        disabled={paginatedPackages.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 250, position: 'sticky !important' }}>
                      <TableSortLabel
                        active={sort === 'name'}
                        direction={sort === 'name' ? (order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, position: 'sticky !important' }}>Rewards</TableCell>
                    <TableCell align="center" sx={{ width: 220, position: 'sticky !important' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPackages.map((pkg) => (
                    <TableRow key={pkg.id} sx={{ py: 3 }}>
                      <TableCell>
                        <Checkbox
                          checked={selectedPackages.has(pkg.id!)}
                          onChange={() => handleTogglePackage(pkg.id!)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">{pkg.name}</Typography>
                      </TableCell>
                      <TableCell>{getRewardsSummary(pkg.rewards || [])}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Assign to employee">
                          <IconButton size="medium" onClick={() => handleOpenAssignDialog(pkg)}>
                            <UserAddOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy package">
                          <IconButton size="medium" onClick={() => handleOpenCopyDialog(pkg)}>
                            <CopyOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit package">
                          <IconButton size="medium" onClick={() => handleEditPackage(pkg)}>
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete package">
                          <IconButton size="medium" color="error" onClick={() => handleOpenDeleteDialog(pkg)}>
                            <DeleteOutlined />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" sx={{ px: '20px' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={filteredPackages.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Create button under table */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <AnimateButton>
          <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleCreatePackage}>
            Create Package
          </Button>
        </AnimateButton>
      </Box>

      {/* Bulk action bar */}
      <ButtonActionBar show={selectedPackages.size > 0} maxWidth="900px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedPackages.size} package{selectedPackages.size > 1 ? 's' : ''} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" onClick={handleClearSelection}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleOpenBulkDeleteDialog} disabled={isDeletingPackages}>
            {isDeletingPackages ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </Box>
      </ButtonActionBar>

      {/* Copy Dialog */}
      <CopyPackageDialog
        open={copyDialogOpen}
        onClose={handleCloseCopyDialog}
        onSubmit={handleCopyPackage}
        originalName={packageToCopy?.name || ''}
        isLoading={isCopyingPackage}
      />

      {/* Assign Dialog */}
      <AssignPackageDialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        onSubmit={handleAssignPackage}
        pkg={packageToAssign}
        isLoading={isAssigningPackage}
      />

      {/* Delete Single Package Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Package</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the package <strong>"{packageToDelete?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button variant="contained" color="warning" onClick={handleCloseDeleteDialog}>
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <Button variant="contained" color="error" onClick={handleConfirmDeletePackage}>
              Delete
            </Button>
          </AnimateButton>
        </DialogActions>
      </Dialog>

      {/* Delete Multiple Packages Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={handleCloseBulkDeleteDialog}>
        <DialogTitle>Delete Packages</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedPackages.size}</strong> selected package
            {selectedPackages.size !== 1 ? 's' : ''}?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button
              variant="contained"
              color="warning"
              onClick={handleCloseBulkDeleteDialog}
              disabled={isDeletingPackages}
            >
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteSelectedPackages}
              disabled={isDeletingPackages}
            >
              {isDeletingPackages ? 'Deleting...' : 'Delete'}
            </Button>
          </AnimateButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

const RewardPackagesPage: React.FC = () => {
  return (
    <Suspense fallback={<CircularProgress />}>
      <RewardPackagesSubComponent />
    </Suspense>
  )
}

export default RewardPackagesPage
